import { ReactComponent as CheckCircle218 } from '@trv-ebus/tds-icons/dist/assets/icons/functional/svg/check-circle-2-18.svg';
import { ReactComponent as XCircle118 } from '@trv-ebus/tds-icons/dist/assets/icons/functional/svg/x-circle-1-18.svg';
import { TdsAlert, TdsButton, TdsButtonContainer, TdsField } from '@trv-tds/react';
import { GlobalContext } from 'context/GlobalProvider';
import _every from 'lodash/every';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import _values from 'lodash/values';
import React, { useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { getSecurityQuestions, validateProfile } from '../../apis/pa-services';
import { CancelModal, Stepper } from '../../components/shared';
import { useReloadRedirect } from '../../hooks';
import { removeTravAuthCookies } from '../../utils/common';
import { RESPONSES, STEPPER_TEXT, STEP_STATUS, COMMON_PASSWORD_ERROR } from '../../utils/constants';
import {
    metricsCancelClickTag,
    metricsElementTag,
    metricsPage,
    metricsPageViewTag
} from '../../utils/coremetrics';
import {
    getMaxlengthAlertText,
    getMinlengthAlertText,
    getNoSpecialCharsAlertText,
    getPassInvalidWordAlertText,
    getPassIsUserIdAlertText,
    getPassRulesInvalidAlertText,
    getRequiredAlertText,
    getSecuritySpecialCharsAlertText
} from '../../utils/validation';
import './styles.scss';
import ScrollToTop from '../../components/shared/ScrollToTop';

const steps = [
    {
        progress: STEP_STATUS.COMPLETE,
        text: STEPPER_TEXT.FIRST_STEP
    },
    {
        progress: STEP_STATUS.COMPLETE,
        text: STEPPER_TEXT.SECOND_STEP
    },
    {
        progress: STEP_STATUS.CURRENT,
        text: STEPPER_TEXT.THIRD_STEP
    },
    {
        progress: STEP_STATUS.INCOMPLETE,
        text: STEPPER_TEXT.FOURTH_STEP
    }
];

const ERROR_404 = RESPONSES.ERROR_404.error;

export default function CreateProfile() {
    const {
        state,
        actions: { updatePasswordInfo, throwError }
    } = useContext(GlobalContext);

    useReloadRedirect();

    const formValid = () => {
        return Object.values(alerts).every(alertText => _isEmpty(alertText));
    };

    const handleValidationError = errors => {
        const validationError = _find(errors, ({ code }) => code === '8001');
        if (validationError) {
            const { message } = validationError;
            const isCommonPassword = message === 'password is commonly used';
            setPasswordError(isCommonPassword ? COMMON_PASSWORD_ERROR : null);
            setError(isCommonPassword ? null : message);
            return true;
        }
        return false;
    };

    const handleValidationSuccess = data => {
        updatePasswordInfo({
            userId: userId,
            passwordTxt: password,
            securityQuestions: [{ answerTxt: securityAnswer, questionTxt: securityQuestion }]
        });
        removeTravAuthCookies();
        window.location.replace(data.loginUrl);
    };

    const continueClick = async () => {
        metricsElementTag(metricsPage.createProfile, false);
        setContinueClicked(true);
        setBusy(true);
        if (!formValid()) {
            setBusy(false);
            return;
        }
        const securityQuestions = [{ answerTxt: securityAnswer, questionTxt: securityQuestion }];
        const { data, errors } = await validateProfile({
            ...state,
            userId,
            passwordTxt: password,
            securityQuestions
        });
        if (data) {
            handleValidationSuccess(data);
        } else if (handleValidationError(errors)) {
            setBusy(false);
            focusSitewideAlert();
            window.scrollTo(0, 0);
        } else {
            throwError('404', ERROR_404);
        }
    };

    //Password create and confirm inputs
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [is8char, set8char] = useState(false);
    const [isMax15char, setMax15char] = useState(false);
    const [isUpperCase, setUpperCase] = useState(false);
    const [isLowerCase, setLowerCase] = useState(false);
    const [hasNumber, setHasNumber] = useState(false);
    const [hasNotTriple, setHasNotTriple] = useState(false);
    const [hasNotLeadingSpace, setHasNotLeadingSpace] = useState(false);
    const [hasSpecialChar, setSpecialChar] = useState(false);
    const [hasNotPassword, setHasNotPassword] = useState(false);
    const [validPassword, setValidPassword] = useState(false);
    const [pwdCheck, setPwdCheck] = useState(false);
    // ID create and confirm inputs
    const [userId, setUserId] = useState('');
    const [confirmId, setConfirmId] = useState('');
    //Security Question selection, response, and completion
    const [securityQuestion, setSecurityQuestion] = useState(null);
    const [securityQuestionsData, setSecurityQuestionsData] = useState(null);
    const [securityAnswer, setSecurityAnswer] = useState('');
    const [alerts, setAlerts] = useState({
        password: '',
        confirmPassword: '',
        id: '',
        confirmId: '',
        securityQuestion: '',
        securityAnswer: ''
    });
    //cancel/continue click
    const [continueClicked, setContinueClicked] = useState(false);
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [busy, setBusy] = useState(false);

    // Helper functions for field state computations
    const getIdAlert = () => (continueClicked && alerts.id ? 'error' : '');
    const getIdMessage = () => (continueClicked ? alerts.id : '');

    const getConfirmIdAlert = () => (_isEmpty(alerts.id) && alerts.confirmId ? 'error' : '');
    const getConfirmIdMessage = () => (_isEmpty(alerts.id) ? alerts.confirmId : '');
    const isConfirmIdDisabled = () => !_isEmpty(alerts.id);

    const getPasswordAlert = () =>
        (continueClicked && alerts.password) || passwordError ? 'error' : '';
    const getPasswordMessage = () => (continueClicked ? alerts.password || passwordError : '');

    const getConfirmPasswordAlert = () =>
        _isEmpty(alerts.password) && alerts.confirmPassword ? 'error' : '';
    const getConfirmPasswordMessage = () =>
        _isEmpty(alerts.password) ? alerts.confirmPassword : '';
    const isConfirmPasswordDisabled = () => !_isEmpty(alerts.password);

    const getSecurityQuestionAlert = () =>
        continueClicked && alerts.securityQuestion ? 'error' : '';
    const getSecurityQuestionMessage = () => (continueClicked ? alerts.securityQuestion : '');

    const getSecurityAnswerAlert = () => (continueClicked && alerts.securityAnswer ? 'error' : '');
    const getSecurityAnswerMessage = () => (continueClicked ? alerts.securityAnswer : '');

    const getErrorAlertClass = () => (error ? '' : 'tds-d-none');

    useEffect(() => {
        metricsPageViewTag(metricsPage.createProfile);
    }, []);

    //Grabs password recovery questions
    useEffect(() => {
        (async () => {
            const { data } = await getSecurityQuestions();
            if (data) {
                setSecurityQuestionsData(data);
            } else throwError('404', ERROR_404);
        })();
    }, [throwError]);

    //Sets the password and determines if it meets requirements and matches
    useEffect(() => {
        let passwordLength = new RegExp(/.{8,}/); //checks if 8 or more characters
        let passwordMaxLength = new RegExp(/^.{1,15}$/);
        let passwordUpperCaseLetters = new RegExp(/[A-Z]/); //checks if it includes a capital letter
        let passwordLowerCaseLetters = new RegExp(/[a-z]/); //checks if includes a lowercase letter
        let passwordNumbers = new RegExp(/[0-9]/); //checks if includes a number
        let passwordSpecialCharacters = new RegExp(/[{}()|;:.,~!?@#$%^&*\-_+=]/); //checks if includes a special character
        let passwordTripleCharacters = new RegExp(/([A-Za-z0-9])\1\1/i);
        // let passwordLeadingSpaces = new RegExp(/^\s+|\s+$/g);
        set8char(passwordLength.test(password));
        setMax15char(passwordMaxLength.test(password));
        setUpperCase(passwordUpperCaseLetters.test(password));
        setLowerCase(passwordLowerCaseLetters.test(password));
        setHasNumber(passwordNumbers.test(password));
        setSpecialChar(passwordSpecialCharacters.test(password));
        setHasNotTriple(!_isEmpty(password) && !passwordTripleCharacters.test(password));
        setHasNotLeadingSpace(!_isEmpty(password) && password === password.trim());
        setHasNotPassword(!_isEmpty(password) && !password.toLowerCase().includes('password'));
        setPasswordError(null);
        //If password meets requirements...
        setValidPassword(
            is8char &&
                isMax15char &&
                isUpperCase &&
                isLowerCase &&
                hasNumber &&
                hasSpecialChar &&
                hasNotTriple &&
                hasNotLeadingSpace &&
                hasNotPassword
        );
    }, [
        is8char,
        isMax15char,
        isLowerCase,
        isUpperCase,
        hasNumber,
        validPassword,
        password,
        hasSpecialChar,
        confirmPassword,
        pwdCheck,
        hasNotTriple,
        hasNotLeadingSpace,
        hasNotPassword
    ]);

    useEffect(() => {
        setAlerts({
            id:
                getRequiredAlertText(userId) ||
                getMinlengthAlertText(userId, 6) ||
                getMaxlengthAlertText(userId, 50) ||
                getNoSpecialCharsAlertText(userId),
            confirmId: userId === confirmId ? '' : "Valid ID's do not match.",
            password:
                getRequiredAlertText(password) ||
                getMaxlengthAlertText(password, 255) ||
                getPassInvalidWordAlertText(password) ||
                getPassIsUserIdAlertText(password, userId) ||
                getPassRulesInvalidAlertText(validPassword),
            confirmPassword: password === confirmPassword ? '' : 'Valid passwords do not match.',
            securityQuestion: getRequiredAlertText(securityQuestion),
            securityAnswer:
                getRequiredAlertText(securityAnswer) ||
                getMinlengthAlertText(securityAnswer, 4) ||
                getMaxlengthAlertText(securityAnswer, 20) ||
                getSecuritySpecialCharsAlertText(securityAnswer)
        });
    }, [
        userId,
        confirmId,
        password,
        confirmPassword,
        securityQuestion,
        securityAnswer,
        validPassword
    ]);

    const greenCheckmark = testId => (
        <CheckCircle218
            className="tds-icon-18"
            data-testid={'greenCheckmark' + testId}
            color="#218d49"
        />
    );
    const redXmark = testId => (
        <XCircle118 className="tds-icon-18" data-testid={'redXmark' + testId} color="#af1214" />
    );

    const buildPasswordReq = (id, condition, testId, message) => ({
        id,
        showCheckmark: condition && greenCheckmark(testId),
        xCheck: pwdCheck && !condition ? redXmark(testId) : '',
        message
    });

    const buildPasswordReqList = () => [
        buildPasswordReq(1, is8char, 1, 'At least 8 characters'),
        buildPasswordReq(2, isMax15char, 2, 'Should not exceed 15 characters'),
        buildPasswordReq(3, isUpperCase, 3, 'One or more uppercase letters'),
        buildPasswordReq(4, isLowerCase, 4, 'One or more lowercase letters'),
        buildPasswordReq(5, hasNumber, 5, 'One or more numbers'),
        buildPasswordReq(6, hasSpecialChar, 6, 'One or more special characters'),
        buildPasswordReq(7, hasNotTriple, 7, 'Does not match 3 or more repeating characters'),
        buildPasswordReq(8, hasNotLeadingSpace, 8, 'Does not have leading or trailing whitespace'),
        buildPasswordReq(9, hasNotPassword, 9, 'Does not contain password')
    ];

    const renderPasswordRequirements = () =>
        buildPasswordReqList().map(({ id, showCheckmark, xCheck, message }) => (
            <div className="pwd-req" id={'securityRow' + id} key={id}>
                <div className="pwd-req-icon">
                    {showCheckmark}
                    {xCheck}
                </div>
                <div className="pwd-req-msg">{message}</div>
            </div>
        ));

    const focusSitewideAlert = () => {
        document.getElementById('errorAlertMsg').focus();
    };

    const renderSecurityQuestions = () =>
        securityQuestionsData ? (
            <TdsField
                label="SECURITY QUESTION"
                alert={getSecurityQuestionAlert()}
                message={getSecurityQuestionMessage()}
                data-testid="securityQuestion"
            >
                <select
                    id="securityQuestion"
                    name="securityQuestion"
                    defaultValue=""
                    onChange={e => setSecurityQuestion(e.target.value)}
                >
                    <option value="">--Select a Question--</option>
                    {securityQuestionsData.map(question => (
                        <option key={question.code} value={question.name}>
                            {question.name}
                        </option>
                    ))}
                </select>
            </TdsField>
        ) : (
            <div aria-disabled="true" aria-label="loading" style={{ lineHeight: 1 }}>
                <Skeleton height={15} />
                <Skeleton height={39} />
            </div>
        );

    return (
        <>
            <ScrollToTop />
            <div className="create-profile">
                <Stepper steps={steps}></Stepper>
                <h1
                    data-testid="create-profile-header"
                    className="tds-h1--article reg-header center"
                >
                    Create Profile
                </h1>
                <form>
                    <div className="tds-row tds-justify-content-center">
                        <div className="tds-col-12">
                            <p className={'page-caption'}>
                                For verification and security purposes please create a user id and
                                password that will replace the Temporary User ID and PIN utilized in
                                this registration process.
                            </p>
                            <TdsAlert
                                data-testid="errorAlert"
                                className={getErrorAlertClass()}
                                type="error"
                                id="errorAlertMsg"
                            >
                                {error}
                            </TdsAlert>
                            <fieldset className="tds-row">
                                <div className="tds-col-md-6 tds-col-sm-12">
                                    <TdsField
                                        label="USER ID"
                                        alert={getIdAlert()}
                                        message={getIdMessage()}
                                        data-testid="userId"
                                    >
                                        <input
                                            type="text"
                                            id="userId"
                                            aria-required="true"
                                            placeholder="Create your User ID"
                                            onChange={e => setUserId(e.target.value)}
                                            autoComplete="username"
                                        />
                                    </TdsField>
                                    <TdsField
                                        label="CONFIRM USER ID"
                                        alert={getConfirmIdAlert()}
                                        message={getConfirmIdMessage()}
                                        data-testid="confirmUserId"
                                    >
                                        <input
                                            type="text"
                                            id="confirmUserId"
                                            aria-required="true"
                                            disabled={isConfirmIdDisabled()}
                                            placeholder="Confirm your User ID"
                                            onChange={e => setConfirmId(e.target.value)}
                                            autoComplete="off"
                                        />
                                    </TdsField>
                                </div>
                            </fieldset>
                            <fieldset className="tds-row">
                                <div className="tds-col-md-6 tds-col-sm-12">
                                    <TdsField
                                        label="PASSWORD"
                                        alert={getPasswordAlert()}
                                        message={getPasswordMessage()}
                                        data-testid="createProfilePassword"
                                    >
                                        <input
                                            type="password"
                                            id="createProfilePassword"
                                            aria-required="true"
                                            placeholder="Create your Password"
                                            onChange={e => setPassword(e.target.value)}
                                            autoComplete="new-password"
                                        />
                                    </TdsField>
                                    <TdsField
                                        label="CONFIRM PASSWORD"
                                        alert={getConfirmPasswordAlert()}
                                        message={getConfirmPasswordMessage()}
                                        data-testid="createProfileConfirmPassword"
                                    >
                                        <input
                                            type="password"
                                            id="createProfileConfirmPassword"
                                            aria-required="true"
                                            disabled={isConfirmPasswordDisabled()}
                                            placeholder="Confirm your Password"
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            onBlur={() => setPwdCheck(true)}
                                            autoComplete="off"
                                        />
                                    </TdsField>
                                </div>
                                <div
                                    id="password-requirements"
                                    className="tds-col-md-6 tds-col-sm-12"
                                >
                                    <legend className="tds-h4">Your password must include:</legend>
                                    {renderPasswordRequirements()}
                                </div>
                            </fieldset>
                            <fieldset>
                                <legend className="tds-h4">Password Recovery Questions</legend>
                                <p className={'pw-ques'}>
                                    If you forgot your password we&apos;ll ask you your secret
                                    questions to verify your identity.
                                </p>
                                {renderSecurityQuestions()}
                                <TdsField
                                    label="ANSWER"
                                    alert={getSecurityAnswerAlert()}
                                    message={getSecurityAnswerMessage()}
                                    data-testid="securityAnswer"
                                >
                                    <input
                                        type="text"
                                        id="securityAnswer"
                                        aria-required="true"
                                        placeholder="Answer your question"
                                        value={securityAnswer}
                                        className="tlPrivate"
                                        onChange={e => setSecurityAnswer(e.target.value)}
                                    />
                                </TdsField>
                            </fieldset>
                        </div>
                    </div>
                    <TdsButtonContainer class="button-container-fix">
                        <TdsButton
                            variant="primary"
                            busy={busy}
                            disabled={busy}
                            onClick={continueClick}
                        >
                            Continue
                        </TdsButton>
                        <TdsButton
                            variant="secondary"
                            busy={busy}
                            data-trigger-modal="cancel-modal"
                            onClick={() => metricsCancelClickTag()}
                        >
                            Cancel
                        </TdsButton>
                    </TdsButtonContainer>
                </form>
                <CancelModal />
            </div>
        </>
    );
}
